import ProductPrice from '../ProductPrice';
import ProductBuySectionReviews from '../ProductBuySectionReviews';
import PdpDealsBannerList from '@components/product/PdpDealsBannerList';
import ProductMemberPricing from '../ProductMemberPricing';
import { MecProduct, PriceType } from '@adapters/bigcommerce/types/product';
import getPdpDealsBannerData from '@adapters/prismic/fetching/pdpDealsBanner/getPdpDealsBannerData';
import { usePdp } from '@context/PdpContext';
import { useTranslation } from 'next-i18next';
import { memo, useEffect, useMemo, useState } from 'react';
import { useLocale } from '@hooks/useLocale';
import IconWrapper from '@components/ui/Icons';
import DealsBanner from '_components/UI/DealsBanner';
import Cookies from 'js-cookie';
import {
  applyCodeDrivenSlot1Cap,
  getDealsBannerMatchDiagnostics,
  getMatchedDealsBanners,
  getResolvedDealsBanners,
} from './getResolvedDealsBanners';
import {
  BANNER_SUMMARY_COLUMNS,
  buildBannerSummaryTable,
  formatDatePST,
  formatInstantPST,
  getBannerBlockers,
  getPrismicEditUrl,
  toRuleDetailRows,
} from './dealsBannerDebug';
import getDealsBannerStickerTypes from '@components/product/getDealsBannerStickerTypes';
import {
  FEATURE_FLAG_DEALS_BANNER,
  FEATURE_FLAG_DEALS_BANNER_SALE,
  FEATURE_FLAG_PDP_2026_1,
} from '@config/feature-flag';

import styles from './ProductPriceAndReviewsContainer.module.scss';

interface ProductPriceAndReviewsContainerProps {
  isGiftCard: boolean;
  reviewSummary: MecProduct['reviewSummary'];
  name: MecProduct['name'];
  scrollToReviews: () => void;
  productOutOfStock?: boolean;
}

const PDP_DEALS_BANNER_DEBUG_COOKIE = 'debugDealsBanner';
const PDP_DEALS_BANNER_DEBUG_COOKIE_OPTIONS = { path: '/' };

const ProductPriceAndReviewsContainer = ({
  isGiftCard,
  reviewSummary,
  name,
  scrollToReviews,
  productOutOfStock = false,
}: ProductPriceAndReviewsContainerProps) => {
  const {
    brand,
    categoryTargets,
    price,
    hasInCartDiscountTag,
    hasMemberPriceDifference,
    actualPriceList,
    promoBadge,
    productCode,
    selectedColour,
    selectedVariant,
  } = usePdp();

  const { t } = useTranslation('pdp');
  const [endDate, setEndDate] = useState('');
  const locale = useLocale();
  const [priceType, setPriceType] = useState<PriceType>(PriceType.Regular);
  const shouldDebugDealsBanner =
    process.env.NEXT_PUBLIC_PDP_DEALS_BANNER_DEBUG === 'true';

  useEffect(() => {
    if (price?.epochEndDate != null) {
      const localEndDate = new Date(price?.epochEndDate);
      const formattedLocalEndDate = new Intl.DateTimeFormat(locale, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        timeZone: 'America/Vancouver',
      }).format(localEndDate);
      setEndDate(formattedLocalEndDate);
    } else {
      setEndDate('');
    }
    if (price?.priceType) {
      setPriceType(price.priceType);
    }
  }, [locale, price]);

  const pdpDealsBannerEntries = useMemo(() => {
    if (!FEATURE_FLAG_DEALS_BANNER) {
      return [];
    }

    return getPdpDealsBannerData(locale);
  }, [locale]);

  const dealsBannerResolverContext = useMemo(
    () => ({
      brand: brand?.name,
      categoryTargets,
      stickerTypes: getDealsBannerStickerTypes(promoBadge),
      style: selectedColour?.name,
      productCode,
      variantSku: selectedVariant?.sku ?? null,
      activePriceGroup: actualPriceList.groupKey,
    }),
    [
      actualPriceList.groupKey,
      brand?.name,
      categoryTargets,
      promoBadge,
      productCode,
      selectedColour?.name,
      selectedVariant?.sku,
    ]
  );

  const resolvedDealsBanners = useMemo(() => {
    if (!FEATURE_FLAG_DEALS_BANNER) {
      return [];
    }

    return getResolvedDealsBanners(
      pdpDealsBannerEntries,
      dealsBannerResolverContext
    );
  }, [dealsBannerResolverContext, pdpDealsBannerEntries]);

  const slot1DealsBanners = useMemo(() => {
    // Clearance and member-pricing are mutually exclusive: ProductMemberPricing
    // returns null when priceType === Clearance, so both can never be 1 simultaneously.
    const codeDrivenSlot1Count =
      (FEATURE_FLAG_PDP_2026_1 && priceType === PriceType.Clearance ? 1 : 0) +
      (hasMemberPriceDifference && priceType !== PriceType.Clearance ? 1 : 0) +
      (FEATURE_FLAG_DEALS_BANNER_SALE && !!endDate ? 1 : 0);
    return applyCodeDrivenSlot1Cap(
      resolvedDealsBanners.filter((banner) => banner.position === 1),
      codeDrivenSlot1Count
    );
  }, [resolvedDealsBanners, priceType, hasMemberPriceDifference, endDate]);

  const slot2DealsBanners = useMemo(
    () => resolvedDealsBanners.filter((banner) => banner.position === 2),
    [resolvedDealsBanners]
  );

  useEffect(() => {
    if (!FEATURE_FLAG_DEALS_BANNER) {
      return;
    }

    const searchParams =
      typeof window === 'undefined'
        ? null
        : new URLSearchParams(window.location.search);
    const debugDealsBannerParam = searchParams?.get('debugDealsBanner');

    if (debugDealsBannerParam === '1') {
      Cookies.set(
        PDP_DEALS_BANNER_DEBUG_COOKIE,
        '1',
        PDP_DEALS_BANNER_DEBUG_COOKIE_OPTIONS
      );
    } else if (debugDealsBannerParam === '0') {
      Cookies.remove(
        PDP_DEALS_BANNER_DEBUG_COOKIE,
        PDP_DEALS_BANNER_DEBUG_COOKIE_OPTIONS
      );
    }

    const isDebugEnabled =
      shouldDebugDealsBanner ||
      debugDealsBannerParam === '1' ||
      (debugDealsBannerParam !== '0' &&
        Cookies.get(PDP_DEALS_BANNER_DEBUG_COOKIE) === '1');

    if (!isDebugEnabled) {
      return;
    }

    // Computed here (debug-only) rather than in a component-level useMemo so the
    // second full 34-doc resolve does not run for every user on every buy-zone
    // interaction — it is only needed for the diagnostics below.
    const matchedDealsBanners = getMatchedDealsBanners(
      pdpDealsBannerEntries,
      dealsBannerResolverContext
    );

    const diagnostics = pdpDealsBannerEntries.map((entry) =>
      getDealsBannerMatchDiagnostics(entry, dealsBannerResolverContext)
    );
    const renderedBannerIds = new Set(
      [...slot1DealsBanners, ...slot2DealsBanners].map((banner) => banner.id)
    );
    const suppressedBySlotLimit = matchedDealsBanners.filter(
      (banner) => !renderedBannerIds.has(banner.id)
    );
    const debugPayload = {
      context: dealsBannerResolverContext,
      matchedCandidates: matchedDealsBanners,
      renderedBannersSlot1: slot1DealsBanners,
      renderedBannersSlot2: slot2DealsBanners,
      suppressedBySlotLimit,
      diagnostics,
    };

    if (typeof window !== 'undefined') {
      (
        window as typeof window & {
          __PDP_DEALS_BANNER_DEBUG__?: typeof debugPayload;
        }
      ).__PDP_DEALS_BANNER_DEBUG__ = debugPayload;
    }

    const nowPST = formatInstantPST(new Date());

    console.groupCollapsed('[PDP deals banner] resolution');

    console.info('Resolver context', dealsBannerResolverContext);

    if (process.env.NEXT_PUBLIC_NYD_DATE_OVERRIDE === 'true') {
      console.warn(
        'NEXT_PUBLIC_NYD_DATE_OVERRIDE is active — date gating is bypassed (all banners treated as in-window).'
      );
    }

    console.info('gates = enabled · isActive');
    console.table(
      buildBannerSummaryTable(diagnostics, renderedBannerIds),
      BANNER_SUMMARY_COLUMNS
    );

    console.groupCollapsed('✏️ edit in Prismic');
    diagnostics.forEach((d) =>
      console.log(`${d.title || d.id} → ${getPrismicEditUrl(d)}`)
    );
    console.groupEnd();

    // Detail only for banners that did not render — rendered ones need no
    // explanation. Note this set includes matched banners suppressed by the
    // slot cap (matched but not rendered), which is why each row distinguishes
    // its blocker reason below.
    const blocked = diagnostics.filter((d) => !renderedBannerIds.has(d.id));

    if (blocked.length > 0) {
      console.groupCollapsed(`🔴 why blocked (${blocked.length})`);

      blocked.forEach((d) => {
        const icon = d.matched ? '🟡' : '🔴';
        console.groupCollapsed(`${icon} ${d.title || d.id}`);

        if (d.startDate || d.endDate) {
          console.log(
            `window: ${formatDatePST(d.startDate)} → ${formatDatePST(
              d.endDate
            )}  (now ${nowPST})`
          );
        }

        const reasons = d.matched
          ? ['Suppressed by slot cap']
          : getBannerBlockers(d, nowPST);
        reasons.forEach((reason) => console.log(`• ${reason}`));

        console.log(`✏️ edit: ${getPrismicEditUrl(d)}`);

        if (d.includeRuleResults.length > 0) {
          console.groupCollapsed('include rules');
          console.table(toRuleDetailRows(d.includeRuleResults));
          console.groupEnd();
        }

        if (d.excludeRuleResults.length > 0) {
          console.groupCollapsed('exclude rules');
          console.table(toRuleDetailRows(d.excludeRuleResults));
          console.groupEnd();
        }

        console.groupEnd();
      });

      console.groupEnd();
    }

    console.groupEnd();
  }, [
    dealsBannerResolverContext,
    pdpDealsBannerEntries,
    slot1DealsBanners,
    slot2DealsBanners,
    shouldDebugDealsBanner,
  ]);

  return (
    <>
      <div className={styles.priceAndReviews}>
        {!isGiftCard && <ProductPrice />}

        {!isGiftCard && !FEATURE_FLAG_PDP_2026_1 && (
          <ProductBuySectionReviews
            productReviewData={reviewSummary}
            handleClick={scrollToReviews}
            productName={name}
          />
        )}
      </div>
      <div>
        {hasInCartDiscountTag && (
          <div className={styles.inCartDiscount}>
            <span>{t('product.tag.inCart.discount')}</span>
          </div>
        )}
        <ProductMemberPricing />
        {FEATURE_FLAG_PDP_2026_1 &&
          priceType === PriceType.Clearance &&
          !productOutOfStock && (
            <div className={styles.dealsBannerContainer}>
              <DealsBanner
                variant="dealsClearance"
                title={t('product.info.banner.clearance.title')}
              />
            </div>
          )}
        {endDate &&
          !productOutOfStock &&
          (FEATURE_FLAG_DEALS_BANNER_SALE ? (
            <div className={styles.dealsBannerContainer}>
              <DealsBanner
                variant="dealsClearance"
                title={t('product.info.banner.deals.title')}
                firstLine={
                  <span>
                    {t('sale.ends')}{' '}
                    <span data-testid="end-date">{endDate}</span>
                  </span>
                }
              />
            </div>
          ) : (
            <div className={styles.endDate}>
              <IconWrapper
                icon="clock"
                width={16}
                classNames={styles.saleIcon}
                fill="none"
              />{' '}
              <span>
                {t('sale.ends')} <span data-testid="end-date">{endDate}</span>
              </span>
            </div>
          ))}
        {FEATURE_FLAG_DEALS_BANNER && (
          <div className={styles.dealsBannerContainerWrapper}>
            <PdpDealsBannerList
              banners={slot1DealsBanners}
              containerClassName={styles.dealsBannerContainer}
            />
          </div>
        )}
      </div>
    </>
  );
};

export default memo(ProductPriceAndReviewsContainer);
